/**
 * Connection Manager - Monitors health of all backends
 * Handles kernel, Convex, MCP servers, and LLM providers
 */

import { ConvexHttpClient } from "convex/browser";
import type { BackendStatus, ConnectionHealth, SystemMode } from "@/types";

interface ConnectionManagerConfig {
  kernelUrl: string;
  convexUrl: string;
  mcpServers: { id: string; name: string; url: string; enabled: boolean }[];
  llmKeys: { provider: string; apiKey: string }[];
  healthCheckInterval: number; // seconds
}

class ConnectionManager {
  private config: ConnectionManagerConfig;
  private health: ConnectionHealth;
  private listeners: Set<(health: ConnectionHealth) => void> = new Set();
  private healthCheckTimer: NodeJS.Timeout | null = null;
  private currentUserMode: SystemMode = "hybrid";

  constructor() {
    // Default config - will be updated via updateConfig
    this.config = {
      kernelUrl: "http://127.0.0.1:3340",
      convexUrl: "",
      mcpServers: [],
      llmKeys: [],
      healthCheckInterval: 30,
    };

    this.health = {
      kernel: this.createDefaultStatus("Local Kernel", "kernel"),
      convex: this.createDefaultStatus("Convex Cloud", "convex"),
      mcpServers: [],
      llmProviders: [],
    };
  }

  // =========== Public Methods ===========

  /**
   * Start health monitoring
   */
  start(intervalSeconds: number = 30): void {
    this.config.healthCheckInterval = intervalSeconds;
    this.checkAllConnections();
    this.healthCheckTimer = setInterval(() => {
      this.checkAllConnections();
    }, intervalSeconds * 1000);
  }

  /**
   * Stop health monitoring
   */
  stop(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<ConnectionManagerConfig>): void {
    this.config = { ...this.config, ...config };
    this.health.mcpServers = this.config.mcpServers.map((server) =>
      this.createDefaultStatus(server.name, "mcp", server.enabled)
    );
    this.health.llmProviders = this.config.llmKeys.map((key) =>
      this.createDefaultStatus(key.provider, "llm", !!key.apiKey)
    );
    this.checkAllConnections();
  }

  /**
   * Set current user mode preference
   */
  setMode(mode: SystemMode): void {
    this.currentUserMode = mode;
    this.notifyListeners();
  }

  /**
   * Get current health status
   */
  getHealth(): ConnectionHealth {
    return JSON.parse(JSON.stringify(this.health));
  }

  /**
   * Get available modes based on backend status
   */
  getAvailableModes(): SystemMode[] {
    const available: SystemMode[] = [];

    if (this.health.kernel.healthy) {
      available.push("centralized");
    }

    if (this.health.convex.healthy) {
      available.push("decentralized");
    }

    if (this.health.kernel.healthy && this.health.convex.healthy) {
      available.push("hybrid");
    }

    return available.length > 0 ? available : [];
  }

  /**
   * Get recommended mode based on current backend health
   */
  getRecommendedMode(): SystemMode {
    const available = this.getAvailableModes();

    if (available.includes(this.currentUserMode)) {
      return this.currentUserMode; // Keep current if available
    }

    // Priority: hybrid > centralized > decentralized
    if (available.includes("hybrid")) return "hybrid";
    if (available.includes("centralized")) return "centralized";
    if (available.includes("decentralized")) return "decentralized";

    return "hybrid"; // Default fallback
  }

  /**
   * Subscribe to health updates
   */
  subscribe(callback: (health: ConnectionHealth) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Get status stats
   */
  getStats() {
    const allBackends = [
      this.health.kernel,
      this.health.convex,
      ...this.health.mcpServers,
      ...this.health.llmProviders,
    ];

    return {
      total: allBackends.length,
      connected: allBackends.filter((b) => b.connected).length,
      healthy: allBackends.filter((b) => b.healthy).length,
      mcpConnected: this.health.mcpServers.filter((s) => s.connected).length,
      llmReady: this.health.llmProviders.filter((p) => p.healthy).length,
      isRunning: this.healthCheckTimer !== null,
    };
  }

  // =========== Private Methods ===========

  /**
   * Create default backend status object
   */
  private createDefaultStatus(
    name: string,
    type: BackendStatus["type"],
    connected: boolean = false
  ): BackendStatus {
    return {
      name,
      type,
      connected,
      healthy: false,
      lastCheck: new Date(),
    };
  }

  /**
   * Check all backend connections
   */
  private async checkAllConnections(): Promise<void> {
    await Promise.all([
      this.checkKernel(),
      this.checkConvex(),
      this.checkMCPServers(),
      this.checkLLMProviders(),
    ]);

    this.notifyListeners();
  }

  /**
   * Check if running in production (HTTPS deployed)
   */
  private isProduction(): boolean {
    return typeof window !== "undefined" && window.location.protocol === "https:";
  }

  /**
   * Check local kernel health
   */
  private async checkKernel(): Promise<void> {
    // Skip localhost checks in production (HTTPS can't call HTTP localhost)
    if (this.isProduction() && this.config.kernelUrl.includes("127.0.0.1")) {
      this.health.kernel = {
        ...this.health.kernel,
        connected: false,
        healthy: false,
        lastCheck: new Date(),
        details: "Kernel not available in cloud deployment",
      };
      return;
    }

    const startTime = Date.now();

    try {
      const response = await fetch(`${this.config.kernelUrl}/status`, {
        method: "GET",
        signal: AbortSignal.timeout(5000),
      });

      const latency = Date.now() - startTime;

      if (response.ok) {
        await response.json();
        this.health.kernel = {
          ...this.health.kernel,
          connected: true,
          healthy: true,
          latency,
          lastCheck: new Date(),
          details: `Running on port 3340`,
        };
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      this.health.kernel = {
        ...this.health.kernel,
        connected: false,
        healthy: false,
        lastCheck: new Date(),
        error: error instanceof Error ? error.message : String(error),
        details: "Kernel offline or unreachable",
      };
    }
  }

  /**
   * Check Convex cloud health
   */
  private async checkConvex(): Promise<void> {
    if (!this.config.convexUrl) {
      this.health.convex = {
        ...this.health.convex,
        connected: false,
        healthy: false,
        lastCheck: new Date(),
        error: "Convex URL not configured",
      };
      return;
    }

    try {
      const client = new ConvexHttpClient(this.config.convexUrl);

      // Try to ping Convex with a simple query
      const startTime = Date.now();

      // We'll just check if we can connect (no actual query needed for health check)
      // In real implementation, you'd query a health_check table
      // await client.query("__system__/version" as any); // This may not exist, so we'll catch it
      // Just check that client exists and is configured
      void client;

      const latency = Date.now() - startTime;

      this.health.convex = {
        ...this.health.convex,
        connected: true,
        healthy: true,
        latency,
        lastCheck: new Date(),
        details: "Connected to Convex cloud",
      };
    } catch (error) {
      this.health.convex = {
        ...this.health.convex,
        connected: false,
        healthy: false,
        lastCheck: new Date(),
        error: error instanceof Error ? error.message : String(error),
        details: "Convex connection failed",
      };
    }
  }

  /**
   * Check MCP servers health
   */
  private async checkMCPServers(): Promise<void> {
    const checks = this.config.mcpServers.map(async (server) => {
      if (!server.enabled) {
        return this.createServerStatus(server.id, server.name, false);
      }

      try {
        const startTime = Date.now();
        const response = await fetch(`${server.url}/health`, {
          method: "GET",
          signal: AbortSignal.timeout(5000),
        });

        const latency = Date.now() - startTime;

        if (response.ok) {
          return this.createServerStatus(server.id, server.name, true, true, latency);
        }
        return this.createServerStatus(server.id, server.name, false);
      } catch (error) {
        return this.createServerStatus(server.id, server.name, false);
      }
    });

    this.health.mcpServers = await Promise.all(checks);
  }

  /**
   * Check LLM providers availability
   */
  private async checkLLMProviders(): Promise<void> {
    const providers: BackendStatus[] = [];

    for (const provider of this.config.llmKeys) {
      if (!provider.apiKey) {
        providers.push({
          ...this.createDefaultStatus(provider.provider, "llm", false),
          details: "API key not configured",
        });
        continue;
      }

      // Validate API key format (basic check)
      const isValid = provider.apiKey.length > 20;

      providers.push({
        ...this.createDefaultStatus(provider.provider, "llm", true),
        healthy: isValid,
        lastCheck: new Date(),
        details: isValid ? "API key configured" : "Invalid API key",
      });
    }

    this.health.llmProviders = providers;
  }

  /**
   * Helper to create MCP server status
   */
  private createServerStatus(
    id: string,
    name: string,
    connected: boolean,
    healthy: boolean = false,
    latency?: number
  ): BackendStatus {
    return {
      name,
      type: "mcp",
      connected,
      healthy,
      latency,
      lastCheck: new Date(),
      details: connected ? `MCP Server: ${id}` : "Offline",
    };
  }

  /**
   * Notify all listeners of health updates
   */
  private notifyListeners(): void {
    const healthSnapshot = this.getHealth();
    this.listeners.forEach((callback) => callback(healthSnapshot));
  }
}

// Export singleton instance
export const connectionManager = new ConnectionManager();
